import { useEffect, useState } from 'react'
import { easeOutExpo, useRevealOnce } from '#app/utils/motion.ts'

const defaultFormat = (n: number) => Math.round(n).toLocaleString('nb-NO')

/**
 * A number that counts up from zero the first time it scrolls into view.
 * Server render, no-JS, reduced motion and numbers already on screen at load
 * all show the final value straight away; assistive tech always gets the
 * final value.
 */
export function CountUp({
	value,
	duration = 1400,
	format = defaultFormat,
	className,
}: {
	value: number
	duration?: number
	format?: (n: number) => string
	className?: string
}) {
	const [ref, reveal] = useRevealOnce<HTMLSpanElement>()
	const [shown, setShown] = useState<number | null>(null)

	useEffect(() => {
		if (reveal !== 'revealed') return
		let frame = 0
		const start = performance.now()
		const step = (t: number) => {
			const progress = Math.min(1, (t - start) / duration)
			setShown(value * easeOutExpo(progress))
			if (progress < 1) frame = requestAnimationFrame(step)
		}
		frame = requestAnimationFrame(step)
		return () => cancelAnimationFrame(frame)
	}, [reveal, value, duration])

	const display =
		reveal === 'armed' ? 0 : reveal === 'revealed' ? (shown ?? 0) : value

	return (
		<span ref={ref} className={className}>
			<span aria-hidden="true">{format(display)}</span>
			<span className="sr-only">{format(value)}</span>
		</span>
	)
}
